import { buildFinishedPartId } from '@calculations/utilities/build-finished-part-id';
import { PayloadAction } from '@reduxjs/toolkit';
import { CraftProductOptionsOptionGroup } from '@snap-types/models/craft/product-options';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import { onPartProductOptionChange } from '../calculations/recalculate/on-part-product-option-change';
import { getQuoteLineOrThrow } from '../utilities/action-guards';

interface Params {
  name: string;
  quoteLineUuid: string;
  productOptionUuid: string;
  craftProductOptionGroup: CraftProductOptionsOptionGroup;
  priceStrategyConfigs: PriceStrategyConfigWithRelations[];
}

export function productOptionSelected(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<Params>,
) {
  const {
    quoteLineUuid,
    name,
    productOptionUuid,
    craftProductOptionGroup,
    priceStrategyConfigs,
  } = action.payload;

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  const marketCode = state.marketCode;

  const { partId } = quoteLine;

  if (
    !marketCode ||
    !quoteLine.marketPrices ||
    !quoteLine.craftProductOptions ||
    !quoteLine.productOptions ||
    // NOTE(mrs): Service charges don't have product options
    quoteLine.serviceChargeUuid ||
    !partId
  ) {
    return;
  }

  const selectedOption = craftProductOptionGroup.options.find(
    (option) => option.name === name,
  );

  if (!selectedOption) {
    return;
  }

  const partOption = quoteLine.productOptions[productOptionUuid];

  partOption.code = selectedOption.code;
  partOption.optionName = name;

  let changesUnitPrice = false;

  for (const quotePriceUuid of quoteLine.quotePriceUuids) {
    const price = onPartProductOptionChange(state, {
      quoteLineUuid,
      quotePriceUuid,
      priceStrategyConfigs,
    });
    if (price.changesUnitPrice) {
      changesUnitPrice = true;
    }
  }

  quoteLine.productOptionsChangeUnitPrice = changesUnitPrice;
  quoteLine.suggestedPricesStatus = SnapSuggestionStatus.ACCEPTED;

  const allQuantitiesAutomated = Object.values(quoteLine.quotePrices).every(
    (quotePrice) => {
      return quotePrice.automatedQuantity?.eq(quotePrice.quantity);
    },
  );

  quoteLine.automatable =
    quoteLine.leadTimeSuggestionStatus === SnapSuggestionStatus.ACCEPTED &&
    allQuantitiesAutomated;

  const finishedPartId = buildFinishedPartId({
    partId,
    craftProductOptions: quoteLine.craftProductOptions,
    productOptions: Object.values(quoteLine.productOptions),
  });

  if (!finishedPartId.isSuccess) {
    return;
  }

  quoteLine.finishedPartId = finishedPartId.finishedPartId;
}
